import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Loader2 } from "lucide-react";

const empty = {
  name: "", email: "", phone: "", address: "", city: "", state: "",
  zip_code: "", property_size: "", notes: "", photo: "", tags: [], default_services: [], status: "active",
};
const FIELDS = Object.keys(empty);
const PRESET_SERVICES = ["Lawn Mowing", "Edging & Trimming", "Fertilization", "Leaf Removal", "Aeration", "Landscaping", "Snow Removal", "Cleanup"];
const uid = () => Math.random().toString(36).slice(2, 10);

export default function CustomerFormDialog({ open, onOpenChange, customer, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const editing = customer?.id;

  useEffect(() => {
    if (open) setForm({ ...empty, ...Object.fromEntries(FIELDS.filter((k) => customer?.[k] != null).map((k) => [k, customer[k]])) });
  }, [open, customer]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const svcs = form.default_services || [];
  const svcTotal = svcs.reduce((a, s) => a + (Number(s.price) || 0), 0);
  const addSvc = (name) => setForm((f) => ({ ...f, default_services: [...(f.default_services || []), { id: uid(), name: name || "", price: 0 }] }));
  const updSvc = (sid, k, v) => setForm((f) => ({ ...f, default_services: f.default_services.map((s) => (s.id === sid ? { ...s, [k]: v } : s)) }));
  const rmSvc = (sid) => setForm((f) => ({ ...f, default_services: f.default_services.filter((s) => s.id !== sid) }));

  const save = async () => {
    if (!(form.name || "").trim()) return toast.error("Name is required");
    const payload = {
      ...form,
      name: form.name.trim(),
      default_services: svcs.filter((s) => (s.name || "").trim() || Number(s.price)).map((s) => ({ id: s.id || uid(), name: s.name, price: Number(s.price) || 0 })),
    };
    setSaving(true);
    try {
      const { data } = editing ? await api.put(`/customers/${editing}`, payload) : await api.post("/customers", payload);
      toast.success(editing ? "Customer updated" : `Customer "${data.name}" added`);
      onOpenChange(false);
      onSaved?.(data, !editing);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save customer");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Edit Customer" : "New Customer"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Name *</Label>
            <Input data-testid="customer-name-input" value={form.name} onChange={set("name")} autoFocus />
          </div>
          <div className="space-y-1.5"><Label>Email</Label><Input data-testid="customer-email-input" value={form.email} onChange={set("email")} /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="customer-phone-input" value={form.phone} onChange={set("phone")} /></div>
          <div className="sm:col-span-2 space-y-1.5"><Label>Address</Label><Input data-testid="customer-address-input" value={form.address} onChange={set("address")} /></div>
          <div className="space-y-1.5"><Label>City</Label><Input data-testid="customer-city-input" value={form.city} onChange={set("city")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>State</Label><Input data-testid="customer-state-input" value={form.state} onChange={set("state")} /></div>
            <div className="space-y-1.5"><Label>Zip</Label><Input data-testid="customer-zip-input" value={form.zip_code} onChange={set("zip_code")} /></div>
          </div>
          <div className="sm:col-span-2 space-y-1.5"><Label>Property / Lot Size</Label><Input data-testid="customer-lot-input" value={form.property_size} onChange={set("property_size")} placeholder="e.g. 0.25 acre" /></div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Default Services & Pricing</Label>
            <p className="text-xs text-muted-foreground -mt-1">Auto-loaded onto new jobs when this customer is picked in Scheduling.</p>
            <div className="space-y-2">
              {svcs.map((s) => (
                <div key={s.id} className="flex items-center gap-2" data-testid="default-service-row">
                  <Input className="flex-1" value={s.name} onChange={(e) => updSvc(s.id, "name", e.target.value)} placeholder="Service name" data-testid="default-service-name" />
                  <div className="relative w-28">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input type="number" className="pl-5" value={s.price} onChange={(e) => updSvc(s.id, "price", e.target.value)} data-testid="default-service-price" />
                  </div>
                  <button type="button" onClick={() => rmSvc(s.id)} data-testid="remove-default-service" className="p-2 rounded hover:bg-destructive/10 transition-colors"><X className="h-4 w-4 text-destructive" /></button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Select value="" onValueChange={(v) => addSvc(v)}>
                <SelectTrigger className="flex-1" data-testid="add-default-service-select"><SelectValue placeholder="Add a service…" /></SelectTrigger>
                <SelectContent>{PRESET_SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" onClick={() => addSvc("")} data-testid="add-custom-default-service"><Plus className="h-4 w-4 mr-1" />Custom</Button>
            </div>
            {svcs.length > 0 && (
              <div className="flex justify-between items-center mt-1 rounded-md bg-muted px-3 py-2 text-sm font-semibold" data-testid="default-services-total">
                <span>Default job total</span><span>{money(svcTotal)}</span>
              </div>
            )}
          </div>
          <div className="sm:col-span-2 space-y-1.5"><Label>Notes</Label><Textarea data-testid="customer-notes-input" value={form.notes} onChange={set("notes")} rows={2} /></div>
        </div>
        <DialogFooter className="pt-2 gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cancel-customer-btn">Cancel</Button>
          <Button data-testid="save-customer-btn" onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Add Customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
